<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useKanbanStore } from 'dashboard/stores/kanban';
import ContactAPI from 'dashboard/api/contacts';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

// CRM-04 R2-T7 (§6.1, N12): os contatos do negócio. Adicionar busca entre os contatos da conta; tirar
// devolve as conversas do contato à regra de entrada; os campos (o layout da conta) são por contato.
// Pode quem vê o negócio, e toda recusa é do servidor: o 409 do contato que já tem negócio aberto no
// funil (com o id do outro só para quem o vê), o 404 do contato de outra conta e o 422 do último contato ou
// do que levaria a última conversa (N11).
defineProps({
  canManage: { type: Boolean, default: false },
});

const emit = defineEmits(['manageFields', 'openCard', 'merge']);

const { t } = useI18n();
const kanban = useKanbanStore();

const details = computed(() => kanban.details);
const cardId = computed(() => details.value?.id);
const contatos = computed(() => details.value?.contacts || []);
const fields = computed(() => details.value?.field_layout || []);

// --- campos por contato ----------------------------------------------------
// Um contato aberto por vez; o formulário é o dele. Abre no primeiro (o principal), que é o que o
// painel mostrava antes, com um contato só.
const editando = ref(null);
const form = ref({});
const isSaving = ref(false);

const contatoEmEdicao = computed(() =>
  contatos.value.find(contato => contato.id === editando.value)
);

const NATIVE_KEYS = ['name', 'email', 'phone_number'];

// '' rather than false for a boolean: "not answered" and "no" are different answers, and
// defaulting to false would silently record every contact as a non-partner.
const emptyValueFor = () => '';

const syncForm = ({ preserveEdits }) => {
  const contactData = contatoEmEdicao.value || {};
  const custom = contactData.custom_attributes || {};
  const next = {};

  fields.value.forEach(field => {
    if (preserveEdits && field.key in form.value) {
      next[field.key] = form.value[field.key];
      return;
    }
    const raw = field.native ? contactData[field.key] : custom[field.key];
    next[field.key] = raw ?? emptyValueFor();
  });

  form.value = next;
};

// Outro negócio no painel: abre o primeiro contato dele.
watch(
  cardId,
  () => {
    editando.value = contatos.value[0]?.id ?? null;
  },
  { immediate: true }
);

// Outro contato aberto: descarta o que foi digitado no anterior e carrega os valores dele.
watch(editando, () => syncForm({ preserveEdits: false }), { immediate: true });

// Campos criados, tirados ou reordenados com o painel aberto, ou o negócio relido: pega o conjunto novo
// sem jogar fora o que está sendo digitado.
watch(fields, () => syncForm({ preserveEdits: true }));

const alternar = contato => {
  editando.value = editando.value === contato.id ? null : contato.id;
};

// Split back into the native columns and the custom_attributes jsonb the server expects.
const contactPayload = () => {
  const payload = { custom_attributes: {} };
  fields.value.forEach(field => {
    const value = form.value[field.key];
    if (NATIVE_KEYS.includes(field.key)) {
      if (value !== '' && value !== null) payload[field.key] = value;
    } else {
      payload.custom_attributes[field.key] = value;
    }
  });
  return payload;
};

// The HTML input type each attribute type maps to. `list` and `checkbox` render their
// own controls, so they are absent here on purpose.
const inputTypeFor = type =>
  ({
    number: 'number',
    currency: 'number',
    percent: 'number',
    date: 'date',
    link: 'url',
  })[type] || 'text';

// Relê o negócio depois de mudar algo nele. A mudança já valeu: se só a releitura falhar, diz que não
// carregou, e não que não mudou.
const reler = async opcoes => {
  try {
    await kanban.refreshDeal(cardId.value, opcoes);
  } catch {
    useAlert(t('KANBAN.DRAWER.LOAD_FAILED'));
  }
};

const salvar = async () => {
  const contato = contatoEmEdicao.value;
  if (!contato) return;
  isSaving.value = true;
  try {
    await kanban.updateCardContact(cardId.value, contato.id, contactPayload());
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('KANBAN.DRAWER.CONTACT_SAVE_FAILED')
    );
    return;
  } finally {
    isSaving.value = false;
  }
  useAlert(t('KANBAN.DRAWER.CONTACT_SAVED'));
  // O nome do negócio pode ser o do contato: a face e o cabeçalho vêm do servidor.
  await reler();
};

// --- adicionar -----------------------------------------------------------------
const buscando = ref(false);
const busca = ref('');
const resultados = ref([]);
const pesquisou = ref(false);
const adicionando = ref(null);
// A recusa do servidor ao adicionar: o texto e, no 409, o negócio aberto do contato (só se a pessoa o vê).
const recusa = ref(null);

let espera = null;
let pedido = 0;

const pesquisar = async () => {
  clearTimeout(espera);
  const termo = busca.value.trim();
  pedido += 1;
  const meu = pedido;
  if (!termo) {
    resultados.value = [];
    pesquisou.value = false;
    return;
  }
  try {
    const { data } = await ContactAPI.search(encodeURIComponent(termo));
    // Uma resposta velha não passa por cima da busca mais nova.
    if (meu !== pedido) return;
    const noNegocio = new Set(contatos.value.map(contato => contato.id));
    resultados.value = (data.payload || []).filter(
      contato => !noNegocio.has(contato.id)
    );
    pesquisou.value = true;
  } catch {
    if (meu === pedido) useAlert(t('KANBAN.DRAWER.CONTACT_SEARCH_FAILED'));
  }
};

const aoDigitar = () => {
  clearTimeout(espera);
  espera = setTimeout(pesquisar, 300);
};

onBeforeUnmount(() => clearTimeout(espera));

const abrirBusca = () => {
  buscando.value = true;
  recusa.value = null;
};

const fecharBusca = () => {
  clearTimeout(espera);
  buscando.value = false;
  busca.value = '';
  resultados.value = [];
  pesquisou.value = false;
};

const recusaAoAdicionar = (error, contato) => {
  const status = error?.response?.status;
  if (status === 409)
    return {
      texto: t('KANBAN.DRAWER.CONTACT_ON_OTHER_DEAL', { name: contato.name }),
      cardId: error.response.data?.card_id || null,
      nome: contato.name,
    };
  if (status === 404)
    return { texto: t('KANBAN.DRAWER.CONTACT_NOT_FOUND'), cardId: null };
  return { texto: t('KANBAN.DRAWER.CONTACT_ADD_FAILED'), cardId: null };
};

const adicionar = async contato => {
  if (adicionando.value) return;
  adicionando.value = contato.id;
  recusa.value = null;
  try {
    await kanban.addCardContact(cardId.value, contato.id);
  } catch (error) {
    recusa.value = recusaAoAdicionar(error, contato);
    return;
  } finally {
    adicionando.value = null;
  }
  fecharBusca();
  useAlert(t('KANBAN.DRAWER.CONTACT_ADDED', { name: contato.name }));
  await reler();
};

// --- tirar ---------------------------------------------------------------------
const tirarDialogRef = ref(null);
const aTirar = ref(null);
const tirando = ref(false);

const pedirParaTirar = contato => {
  aTirar.value = contato;
  tirarDialogRef.value?.open();
};

const recusaAoTirar = (error, contato) => {
  if (error?.response?.status !== 422)
    return t('KANBAN.DRAWER.CONTACT_REMOVE_FAILED');
  if (error.response.data?.error === 'last_conversation')
    return t('KANBAN.DRAWER.LAST_CONVERSATION', { name: contato.name });
  return t('KANBAN.DRAWER.LAST_CONTACT');
};

const tirar = async () => {
  const contato = aTirar.value;
  if (!contato || tirando.value) return;
  tirando.value = true;
  try {
    await kanban.removeCardContact(cardId.value, contato.id);
  } catch (error) {
    useAlert(recusaAoTirar(error, contato));
    return;
  } finally {
    tirando.value = false;
    tirarDialogRef.value?.close();
  }
  useAlert(t('KANBAN.DRAWER.CONTACT_REMOVED', { name: contato.name }));
  // As conversas dele voltam à regra de entrada e podem virar negócio no quadro.
  await reler({ board: true });
};
</script>

<template>
  <section class="flex flex-col gap-2" data-secao="contatos">
    <div class="flex items-center gap-2">
      <h3 class="flex-1 text-xs font-medium uppercase text-n-slate-10">
        {{ $t('KANBAN.DRAWER.CONTACTS_SECTION') }}
      </h3>
      <button
        v-if="canManage"
        type="button"
        class="flex items-center gap-1 px-2 h-7 text-xs rounded-md text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
        @click="emit('manageFields')"
      >
        <Icon icon="i-lucide-sliders-horizontal" class="size-3.5" />
        {{ $t('KANBAN.DRAWER.MANAGE_FIELDS') }}
      </button>
      <button
        type="button"
        data-adicionar-contato
        class="flex items-center gap-1 px-2 h-7 text-xs rounded-md text-n-brand hover:bg-n-alpha-2"
        @click="buscando ? fecharBusca() : abrirBusca()"
      >
        <Icon icon="i-lucide-user-plus" class="size-3.5" />
        {{ $t('KANBAN.DRAWER.ADD_CONTACT') }}
      </button>
    </div>

    <div v-if="buscando" class="flex flex-col gap-1">
      <input
        v-model="busca"
        type="search"
        data-busca-contato
        :placeholder="$t('KANBAN.DRAWER.SEARCH_CONTACT')"
        :aria-label="$t('KANBAN.DRAWER.SEARCH_CONTACT')"
        class="w-full h-9 px-2.5 text-sm rounded-lg reset-base bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
        @input="aoDigitar"
        @keydown.enter.prevent="pesquisar"
        @keydown.esc="fecharBusca"
      />
      <button
        v-for="contato in resultados"
        :key="contato.id"
        type="button"
        data-resultado
        :disabled="Boolean(adicionando)"
        class="flex items-center gap-2 p-1.5 text-left rounded-md hover:bg-n-alpha-2 disabled:opacity-50"
        @click="adicionar(contato)"
      >
        <Avatar
          :name="contato.name || $t('KANBAN.CARD.NO_NAME')"
          :src="contato.thumbnail || ''"
          :size="20"
          rounded-full
        />
        <span class="flex flex-col min-w-0">
          <span class="text-sm truncate text-n-slate-12">
            {{ contato.name || $t('KANBAN.CARD.NO_NAME') }}
          </span>
          <span class="text-xs truncate text-n-slate-10">
            {{ contato.phone_number || contato.email }}
          </span>
        </span>
      </button>
      <p v-if="pesquisou && !resultados.length" class="text-xs text-n-slate-10">
        {{ $t('KANBAN.DRAWER.NO_CONTACT_FOUND') }}
      </p>
    </div>

    <div
      v-if="recusa"
      data-recusa
      class="flex items-start gap-2 p-2 text-xs border rounded-lg border-n-amber-6 bg-n-amber-2 text-n-slate-12"
    >
      <Icon icon="i-lucide-info" class="mt-0.5 size-3.5 flex-shrink-0" />
      <div class="flex flex-col gap-1">
        <span>{{ recusa.texto }}</span>
        <div v-if="recusa.cardId" class="flex items-center gap-3">
          <button
            type="button"
            data-abrir-outro
            class="font-medium text-n-brand hover:underline"
            @click="emit('openCard', recusa.cardId)"
          >
            {{ $t('KANBAN.DRAWER.OPEN_OTHER_DEAL') }}
          </button>
          <!-- N12: o administrador pode juntar o outro negócio a este. -->
          <button
            v-if="canManage"
            type="button"
            data-juntar-outro
            class="font-medium text-n-brand hover:underline"
            @click="
              emit('merge', {
                id: recusa.cardId,
                display_name: $t('KANBAN.MERGE.OPEN_DEAL_OF', {
                  name: recusa.nome,
                }),
              })
            "
          >
            {{ $t('KANBAN.MERGE.INTO_THIS') }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-for="contato in contatos"
      :key="contato.id"
      data-contato
      class="flex flex-col border rounded-lg border-n-weak"
    >
      <div class="flex items-center gap-2 p-2">
        <Avatar
          :name="contato.name || $t('KANBAN.CARD.NO_NAME')"
          :src="contato.thumbnail || contato.avatar_url || ''"
          :size="24"
          rounded-full
        />
        <button
          type="button"
          class="flex items-center flex-1 min-w-0 gap-1.5 text-left"
          :aria-expanded="editando === contato.id"
          :aria-label="$t('KANBAN.DRAWER.EDIT_CONTACT_FIELDS')"
          @click="alternar(contato)"
        >
          <span class="text-sm truncate text-n-slate-12">
            {{ contato.name || $t('KANBAN.CARD.NO_NAME') }}
          </span>
          <span
            v-if="contato.principal"
            class="flex-shrink-0 px-1.5 py-0.5 text-xs rounded bg-n-alpha-2 text-n-slate-11"
          >
            {{ $t('KANBAN.DRAWER.PRINCIPAL') }}
          </span>
          <Icon
            :icon="
              editando === contato.id
                ? 'i-lucide-chevron-down'
                : 'i-lucide-chevron-right'
            "
            class="flex-shrink-0 size-3.5 text-n-slate-10"
          />
        </button>
        <button
          type="button"
          data-tirar-contato
          :disabled="contatos.length <= 1"
          :title="
            contatos.length <= 1
              ? $t('KANBAN.DRAWER.LAST_CONTACT')
              : $t('KANBAN.DRAWER.REMOVE_CONTACT')
          "
          :aria-label="$t('KANBAN.DRAWER.REMOVE_CONTACT')"
          class="flex items-center justify-center flex-shrink-0 rounded-md size-7 text-n-slate-11 hover:bg-n-ruby-3 hover:text-n-ruby-11 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-n-slate-11"
          @click="pedirParaTirar(contato)"
        >
          <Icon icon="i-lucide-user-minus" class="size-3.5" />
        </button>
      </div>

      <div
        v-if="editando === contato.id"
        data-campos
        class="flex flex-col gap-3 p-2 border-t border-n-weak"
      >
        <label
          v-for="field in fields"
          :key="field.key"
          class="flex flex-col gap-1 text-xs text-n-slate-11"
        >
          <span>{{
            field.native ? $t(`KANBAN.DRAWER.FIELDS.${field.key}`) : field.label
          }}</span>

          <select
            v-if="field.type === 'list'"
            v-model="form[field.key]"
            class="w-full h-9 px-2 text-sm rounded-lg reset-base bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
          >
            <option value="">{{ $t('KANBAN.DRAWER.EMPTY_FIELD') }}</option>
            <option
              v-for="option in field.values || []"
              :key="option"
              :value="option"
            >
              {{ option }}
            </option>
          </select>

          <select
            v-else-if="field.type === 'checkbox'"
            v-model="form[field.key]"
            class="w-full h-9 px-2 text-sm rounded-lg reset-base bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
          >
            <option value="">{{ $t('KANBAN.DRAWER.EMPTY_FIELD') }}</option>
            <option :value="true">{{ $t('KANBAN.DRAWER.YES') }}</option>
            <option :value="false">{{ $t('KANBAN.DRAWER.NO') }}</option>
          </select>

          <input
            v-else
            v-model="form[field.key]"
            :type="inputTypeFor(field.type)"
            :placeholder="$t('KANBAN.DRAWER.EMPTY_FIELD')"
            class="w-full h-9 px-2.5 text-sm rounded-lg reset-base bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
          />
        </label>

        <button
          type="button"
          data-salvar-contato
          :disabled="isSaving"
          class="h-9 text-sm font-medium rounded-lg bg-n-brand text-white hover:opacity-90 disabled:opacity-50"
          @click="salvar"
        >
          {{ $t('KANBAN.DRAWER.SAVE_CONTACT') }}
        </button>
      </div>
    </div>

    <Dialog
      ref="tirarDialogRef"
      type="alert"
      :title="
        $t('KANBAN.DRAWER.REMOVE_CONTACT_TITLE', { name: aTirar?.name || '' })
      "
      :description="$t('KANBAN.DRAWER.REMOVE_CONTACT_DESCRIPTION')"
      :confirm-button-label="$t('KANBAN.DRAWER.REMOVE_CONTACT')"
      :is-loading="tirando"
      @confirm="tirar"
    />
  </section>
</template>
